import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Coins, ShieldCheck, Menu, X } from "lucide-react";

function NavItem({ to, label }) {
  return (
    <NavLink
      to={to}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        `cm-label transition-colors ${isActive ? "text-copper" : "text-gray-400 hover:text-white"}`
      }
    >
      {label}
    </NavLink>
  );
}

function MobileNavItem({ to, label }) {
  return (
    <NavLink
      to={to}
      data-testid={`mobile-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        `block py-4 cm-heading text-3xl border-b border-ink-700 transition-colors ${
          isActive ? "text-copper" : "text-platinum hover:text-copper"
        }`
      }
    >
      {label}
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile menu on any route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock body scroll + listen for Escape while menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    const onKey = (e) => {
      if (e.key === "Escape" && mobileOpen) setMobileOpen(false);
    };
    if (mobileOpen) document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [mobileOpen]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <a href="#main-content" className="cm-skip-link" data-testid="skip-to-content">
        Skip to main content
      </a>
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-ink-950/80 border-b border-ink-700" role="banner">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link" aria-label="HairMaxxing Pro — home">
            <img src="/logo.png" alt="HairMaxxing Pro" className="cm-logo h-8 md:h-9 w-auto" />
          </Link>

          <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
            {user ? (
              <>
                <NavItem to="/dashboard" label="Dashboard" />
                <NavItem to="/scan" label="New Scan" />
                <NavItem to="/history" label="History" />
                <NavItem to="/buy-credits" label="Credits" />
                <NavItem to="/account" label="Account" />
                {user.role === "admin" && <NavItem to="/admin" label="Admin" />}
              </>
            ) : (
              <>
                <a href="#how" className="cm-label text-gray-400 hover:text-white">How it works</a>
                <a href="#pricing" className="cm-label text-gray-400 hover:text-white">Pricing</a>
              </>
            )}
          </nav>

          <div className="flex items-center gap-3">
            {user ? (
              <>
                <div
                  className="hidden sm:flex items-center gap-2 px-3 py-1.5 border border-ink-700 rounded-full"
                  data-testid="credits-display"
                  title="Free scans remaining + top-up balance"
                >
                  <Coins className="w-4 h-4 text-copper" />
                  <span className="text-sm font-semibold">
                    {(user.scans_remaining_today ?? user.daily_limit ?? 5)}/{user.daily_limit ?? 5}
                    {user.bonus_scans > 0 && (
                      <span className="text-copper ml-1">+{user.bonus_scans}</span>
                    )}
                  </span>
                </div>
                {user.role === "admin" && <ShieldCheck className="w-4 h-4 text-copper hidden sm:block" />}
                <button
                  onClick={handleLogout}
                  data-testid="logout-button"
                  className="cm-btn-secondary hidden md:flex items-center gap-2"
                  aria-label="Log out"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Logout</span>
                </button>
                <button
                  type="button"
                  onClick={() => setMobileOpen((v) => !v)}
                  className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-ink-700 text-platinum hover:border-copper transition-colors"
                  aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
                  aria-expanded={mobileOpen}
                  aria-controls="mobile-menu-panel"
                  data-testid="mobile-menu-toggle"
                  style={{ WebkitTapHighlightColor: "transparent" }}
                >
                  {mobileOpen ? <X className="w-5 h-5" aria-hidden="true" /> : <Menu className="w-5 h-5" aria-hidden="true" />}
                </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="header-login-link" className="cm-btn-secondary hidden sm:inline-flex">Login</Link>
                <Link to="/register" data-testid="header-signup-link" className="cm-btn-primary hidden sm:inline-flex">Start Free</Link>
                <button
                  type="button"
                  onClick={() => setMobileOpen((v) => !v)}
                  className="sm:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-ink-700 text-platinum hover:border-copper transition-colors"
                  aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
                  aria-expanded={mobileOpen}
                  aria-controls="mobile-menu-panel"
                  data-testid="mobile-menu-toggle"
                  style={{ WebkitTapHighlightColor: "transparent" }}
                >
                  {mobileOpen ? <X className="w-5 h-5" aria-hidden="true" /> : <Menu className="w-5 h-5" aria-hidden="true" />}
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Mobile menu panel — rendered OUTSIDE the header so its `fixed`
          positioning is not trapped by the header's backdrop-filter
          containing-block (a known iOS Safari issue). */}
      {mobileOpen && (
        <div
          id="mobile-menu-panel"
          className="md:hidden fixed inset-0 top-16 bg-ink-950 overflow-y-auto z-50 animate-fade-in"
          data-testid="mobile-menu-panel"
          role="dialog"
          aria-modal="true"
          aria-label="Site navigation"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          <nav aria-label="Mobile primary" className="px-6 py-8 flex flex-col">
            {user ? (
              <>
                <div className="flex items-center gap-2 mb-6 px-4 py-3 rounded-full border border-ink-700 self-start">
                  <Coins className="w-4 h-4 text-copper" />
                  <span className="text-sm font-semibold text-platinum">
                    {(user.scans_remaining_today ?? user.daily_limit ?? 5)}/{user.daily_limit ?? 5} free
                    {user.bonus_scans > 0 && (
                      <span className="text-copper ml-1">· +{user.bonus_scans} top-up</span>
                    )}
                  </span>
                </div>
                <MobileNavItem to="/dashboard" label="Dashboard" />
                <MobileNavItem to="/scan" label="New Scan" />
                <MobileNavItem to="/history" label="History" />
                <MobileNavItem to="/buy-credits" label="Credits" />
                <MobileNavItem to="/account" label="Account" />
                {user.role === "admin" && <MobileNavItem to="/admin" label="Admin" />}
                <button
                  onClick={handleLogout}
                  data-testid="mobile-logout-button"
                  className="cm-btn-secondary mt-8 inline-flex items-center justify-center gap-2 w-full"
                >
                  <LogOut className="w-4 h-4" /> Logout
                </button>
              </>
            ) : (
              <>
                <MobileNavItem to="/login" label="Login" />
                <MobileNavItem to="/register" label="Start Free" />
              </>
            )}
          </nav>
        </div>
      )}

      <main id="main-content" className="flex-1" role="main" tabIndex="-1">{children}</main>

      <footer className="border-t border-ink-700 bg-ink-950 mt-24" role="contentinfo">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <img src="/logo.png" alt="HairMaxxing Pro" className="cm-logo h-8 w-auto opacity-90" />
          <div className="flex flex-col md:items-end gap-1 text-xs text-gray-500">
            <a
              href="mailto:Support@hairmaxxingpro.com"
              className="hover:text-copper transition-colors tracking-wider uppercase"
              data-testid="footer-support-email"
            >
              Support@hairmaxxingpro.com
            </a>
            <Link
              to="/accessibility"
              className="hover:text-copper transition-colors tracking-wider uppercase"
              data-testid="footer-accessibility-link"
            >
              Accessibility
            </Link>
            <Link
              to="/privacy"
              className="hover:text-copper transition-colors tracking-wider uppercase"
              data-testid="footer-privacy-link"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="hover:text-copper transition-colors tracking-wider uppercase"
              data-testid="footer-terms-link"
            >
              Terms of Service
            </Link>
            <span className="text-gray-600">© {new Date().getFullYear()} HairMaxxing Pro</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
